import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { View } from "react-native";
import { Button, ButtonText } from "ui/gluestack/button";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import { CreateLocationRequest } from "app/services/api/types/locations/requests/CreateLocationRequest";
import { useCreateLocationMutation } from "app/services/api/locationsApi";
import { useDisplayToast } from "ui/hooks/useDisplayToast";

export interface CreateLocationModalRef {
  show: () => void;
  hide: () => void;
}

export const CreateLocationModal = forwardRef<CreateLocationModalRef, {}>(
  (_, ref) => {
    const [isVisible, setIsVisible] = useState(false);
    const { showErrorToast, showNewToast } = useDisplayToast();
    const [createLocation, { isLoading }] = useCreateLocationMutation();

    const {
      control,
      handleSubmit,
      reset,
      formState: { isValid },
    } = useForm<CreateLocationRequest>({
      defaultValues: {
        name: "",
        address: "",
      },
      mode: "onChange",
    });

    useImperativeHandle(
      ref,
      () => ({
        show: () => {
          setIsVisible(true);
        },
        hide: () => {
          setIsVisible(false);
        },
      }),
      [],
    );

    useEffect(() => {
      if (isVisible) {
        reset();
      }
    }, [isVisible, reset]);

    const handleSave = async (data: CreateLocationRequest) => {
      try {
        await createLocation(data).unwrap();
        setIsVisible(false);

        showNewToast({
          title: "Location created",
          message: "Your new location was created successfully",
          type: "success",
        });
      } catch (error) {
        showErrorToast(error);
      }
    };

    const handleClose = () => {
      reset();
      setIsVisible(false);
    };

    const canSave = isValid && !isLoading;

    return (
      <ReusableModal
        isVisible={isVisible}
        onClose={handleClose}
        title="Add Location"
        hasCancelButton
      >
        <View className="space-y-4">
          <FormControlledInput
            id="location-name"
            name="name"
            label="Location Name"
            placeholder="e.g., Casa iCare"
            control={control}
            isRequired
          />

          <FormControlledInput
            id="location-address"
            name="address"
            label="Location Address"
            placeholder="Optional address"
            control={control}
            isRequired={false}
          />
        </View>

        <View className="flex-row gap-3 mt-6">
          <Button
            variant="outline"
            onPress={handleClose}
            className="flex-1"
            disabled={isLoading}
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            onPress={handleSubmit(handleSave)}
            className="flex-1"
            disabled={!canSave}
          >
            <ButtonText>{isLoading ? "Saving..." : "Add"}</ButtonText>
          </Button>
        </View>
      </ReusableModal>
    );
  },
);

CreateLocationModal.displayName = "CreateLocationModal";
